import { useEffect, useRef, useState } from 'react'

import { readTam, TAM_MAX } from '@aytm/app/stats/page/question-views/vk/lib/utils'

// The slider reaches twice the market size, but never past the largest one the inputs accept, so it
// cannot offer a value the write would then cut down.
const sliderMax = tam => Math.min(TAM_MAX, tam * 2)

export const useTamSection = ({ answer, config, onModelChange }) => {
  const answerTamMax = sliderMax(parseInt(answer?.TAM?.[0], 10))
  const [tamValue, setTamValue] = useState(answer?.TAM?.[0])

  // The field shows what is being typed, and the slider the value it reads as. Floored in the field
  // itself, a cleared or one-digit entry would show 100 and the next key would be appended to it. The
  // field takes the slider's value again once the edit is committed, so a sub-floor entry then reads
  // as the floor it was saved at.
  const [tamText, setTamText] = useState(null)

  // A typed value is judged against the scale the answer to it will bring, not the one it replaces:
  // the slider otherwise draws a value far past its old maximum until the fetch lands. The answer's
  // own scale takes over again once it arrives.
  const [typedTamMax, setTypedTamMax] = useState(null)
  useEffect(() => setTypedTamMax(null), [answerTamMax])
  const tamMaxValue = typedTamMax ?? answerTamMax

  const getTamConfig = value => {
    return {
      ...config,
      tam: readTam(value),
      update: [...config.update, 'tam']
    }
  }

  const onModelChangeWithLoading = value => {
    // see the chart panel's resolvers: a config not yet derived from a real answer has nothing to
    // carry forward, so the slider holds its value rather than persisting an empty one
    if (!Array.isArray(config?.update)) return

    // a value already written would only repeat the round trip, and its loader with it
    if (readTam(value) === config.tam) return

    onModelChange({ loading: true, config: getTamConfig(value) })
  }

  // One timer for the life of the section rather than one per render. Every keystroke re-renders
  // through the value setter, so a writer rebuilt each time never met the timer the previous
  // keystroke started and each edit persisted on its own. The ref keeps the timer stable while
  // still calling through to the current render's writer, which holds the current config.
  const latestWriter = useRef(onModelChangeWithLoading)
  latestWriter.current = onModelChangeWithLoading

  const typingTimer = useRef(null)
  const cancelTyping = () => {
    clearTimeout(typingTimer.current)
    typingTimer.current = null
  }
  useEffect(() => cancelTyping, [])

  const onSlideChange = event => {
    setTamText(null)
    setTamValue(event.value)
  }
  const onSlideEnd = event => {
    if (event.value === 0 || event.value === config.tam) return

    // the slider and the text input share one wait, so a run of releases goes out once
    cancelTyping()
    typingTimer.current = setTimeout(() => {
      typingTimer.current = null
      latestWriter.current(event.value)
    }, 1500)
  }

  const handleInputChange = event => {
    const value = readTam(event.target.value)
    setTamText(event.target.value)
    setTamValue(value)
    setTypedTamMax(sliderMax(value))

    cancelTyping()
    typingTimer.current = setTimeout(() => {
      typingTimer.current = null
      setTamText(null)
      latestWriter.current(value)
    }, 1500)
  }

  // Enter commits what is typed at once and takes the pending write's place, so the edit goes out
  // once. Any other key is still being typed: the input's change starts the wait instead.
  const handleKeyPressChange = event => {
    if ('Enter' !== event.key) return

    cancelTyping()
    setTamText(null)
    setTamValue(readTam(event.target.value))
    onModelChangeWithLoading(event.target.value)
  }

  return {
    tamValue,
    tamText,
    tamMaxValue,
    onSlideChange,
    onSlideEnd,
    handleInputChange,
    handleKeyPressChange
  }
}
